import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Loader2, Send, CalendarClock, X, MessageSquareText } from 'lucide-react';
import { NoteGrid, fmtDT } from './NoteGrid';

const PALETTE = [{ c: 0, bg: '#171717', l: 'leer' }, { c: 63, bg: '#cc3b38', l: 'rot' }, { c: 64, bg: '#e07b2f', l: 'orange' }, { c: 65, bg: '#e6c02f', l: 'gelb' },
  { c: 66, bg: '#3f9b4f', l: 'grün' }, { c: 67, bg: '#2f5fcf', l: 'blau' }, { c: 68, bg: '#7a3fcf', l: 'violett' }, { c: 69, bg: '#f5f5f5', l: 'weiß' }];
const RAINBOW = [63, 64, 65, 66, 67, 68];
// Mirrors the backend normalisation so the counter matches what the Note can show.
const normalize = (t) => (t || '').toUpperCase().replace(/Ä/g, 'AE').replace(/Ö/g, 'OE').replace(/Ü/g, 'UE').replace(/ß/g, 'SS').replace(/[^A-Z0-9 !@#$()\-+&=;:'"%,.\/?]/g, '');

export const CustomMessageCard = ({ defaultSeconds = 600, onChanged }) => {
  const [rows, setRows] = useState(['', '', '']);
  const [align, setAlign] = useState('center');
  const [tilesRow, setTilesRow] = useState(0);
  const [tiles, setTiles] = useState(Array.from({ length: 15 }, (_, i) => RAINBOW[i % 6]));
  const [brush, setBrush] = useState(63);
  const [minutes, setMinutes] = useState(Math.max(1, Math.round(defaultSeconds / 60)));
  const [at, setAt] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState('');
  const [scheduled, setScheduled] = useState([]);

  const payload = { rows, align, tiles_row: tilesRow, tiles, display_seconds: Math.max(1, Number(minutes)) * 60 };
  const loadScheduled = useCallback(() => api.vbManagerScheduled().then(r => setScheduled(r.data.scheduled || [])).catch(() => {}), []);
  useEffect(() => { loadScheduled(); }, [loadScheduled]);
  useEffect(() => {
    const t = setTimeout(() => api.vbManagerMessagePreview({ rows, align, tiles_row: tilesRow, tiles }).then(r => setPreview(r.data.matrix)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [rows, align, tilesRow, tiles]);

  const push = async () => {
    setBusy('push');
    try {
      const r = await api.vbManagerMessagePush(payload);
      toast.success(r.data.status === 'gate_wait' ? `Eingereiht — Note erlaubt ein Update alle 20 s (in ${r.data.retry_in}s)` : `Nachricht wird ${minutes} Min gezeigt${r.data.status === 'dry_run' ? ' — DRY_RUN' : ''}`);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    setBusy('');
  };
  const schedule = async () => {
    setBusy('schedule');
    try { await api.vbManagerMessageSchedule({ ...payload, at }); toast.success(`Geplant für ${fmtDT(at)}`); setAt(''); loadScheduled(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    setBusy('');
  };
  const unschedule = async (id) => {
    try { const r = await api.vbManagerUnschedule(id); setScheduled(r.data.scheduled || []); toast.success('Geplante Nachricht gelöscht'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  return (
    <div className="bg-card border border-border rounded-sm p-5" data-testid="vb-custom-message">
      <p className="text-sm font-medium text-foreground flex items-center gap-2"><MessageSquareText className="w-4 h-4 text-amber-400" /> Eigene Nachricht</p>
      <p className="text-[11px] text-muted-foreground mt-0.5 mb-3">Drei Zeilen à 15 Zeichen · Großbuchstaben, Ä/Ö/Ü/ß werden umgeschrieben · optional eine Zeile Farbkacheln</p>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="space-y-2">
          {rows.map((r, i) => {
            const n = normalize(r).length;
            const isTiles = tilesRow === i + 1;
            return (
              <div key={i} className="flex items-center gap-2">
                <span className="text-[10px] font-mono text-muted-foreground w-4">{i + 1}</span>
                {isTiles ? (
                  <div className="flex-1 flex items-center gap-[3px]" data-testid={`vb-msg-tiles-row`}>
                    {tiles.map((c, j) => (
                      <button key={j} type="button" onClick={() => setTiles(t => t.map((x, k) => k === j ? brush : x))} style={{ background: PALETTE.find(p => p.c === c)?.bg || '#171717' }}
                        className="w-5 h-6 rounded-[2px] border border-neutral-700" data-testid={`vb-msg-tile-${j}`} />
                    ))}
                  </div>
                ) : (
                  <input value={r} onChange={e => setRows(rs => rs.map((x, k) => k === i ? e.target.value : x))} data-testid={`vb-msg-row-${i}`}
                    className={`flex-1 bg-secondary border rounded-sm px-2 py-1 text-sm font-mono text-foreground focus:outline-none ${n > 15 ? 'border-red-500' : 'border-border focus:border-blue-500'}`} placeholder={`Zeile ${i + 1}`} />
                )}
                <span className={`text-[10px] font-mono w-10 text-right ${n > 15 ? 'text-red-400' : 'text-muted-foreground'}`} data-testid={`vb-msg-count-${i}`}>{isTiles ? '▓' : `${n}/15`}</span>
              </div>
            );
          })}
          <div className="flex items-center gap-3 flex-wrap text-[11px] pt-1">
            <label className="flex items-center gap-1.5 text-muted-foreground">Kacheln in Zeile
              <select value={tilesRow} onChange={e => setTilesRow(Number(e.target.value))} className="bg-secondary border border-border rounded-sm px-1.5 py-0.5 text-xs text-foreground" data-testid="vb-msg-tiles-select">
                <option value={0}>keine</option><option value={1}>1</option><option value={2}>2</option><option value={3}>3</option>
              </select>
            </label>
            {tilesRow > 0 && (
              <div className="flex items-center gap-1" data-testid="vb-msg-palette">
                {PALETTE.map(p => <button key={p.c} type="button" title={p.l} onClick={() => setBrush(p.c)} style={{ background: p.bg }} className={`w-4 h-4 rounded-sm border ${brush === p.c ? 'border-white' : 'border-neutral-700'}`} data-testid={`vb-msg-brush-${p.c}`} />)}
                <button type="button" onClick={() => setTiles(Array.from({ length: 15 }, (_, i) => RAINBOW[i % 6]))} className="ml-1 text-muted-foreground hover:text-foreground" data-testid="vb-msg-rainbow-btn">Regenbogen</button>
                <button type="button" onClick={() => setTiles(Array(15).fill(brush))} className="ml-1 text-muted-foreground hover:text-foreground" data-testid="vb-msg-fill-btn">Alle füllen</button>
              </div>
            )}
            <label className="flex items-center gap-1.5 text-muted-foreground">Ausrichtung
              <select value={align} onChange={e => setAlign(e.target.value)} className="bg-secondary border border-border rounded-sm px-1.5 py-0.5 text-xs text-foreground" data-testid="vb-msg-align">
                <option value="center">zentriert</option><option value="left">links</option>
              </select>
            </label>
            <label className="flex items-center gap-1.5 text-muted-foreground">Anzeigezeit
              <input type="number" min="1" max="1440" value={minutes} onChange={e => setMinutes(e.target.value)} className="w-14 bg-secondary border border-border rounded-sm px-1.5 py-0.5 text-xs font-mono text-foreground text-right" data-testid="vb-msg-minutes" /> Min
            </label>
          </div>
          <div className="flex items-center gap-2 flex-wrap pt-1">
            <Button size="sm" onClick={push} disabled={!!busy} className="h-7 text-[11px] bg-amber-600 hover:bg-amber-500 text-white" data-testid="vb-msg-push-btn">
              {busy === 'push' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />} Jetzt anzeigen
            </Button>
            <input type="datetime-local" value={at} onChange={e => setAt(e.target.value)} className="bg-secondary border border-border rounded-sm px-1.5 py-1 text-xs font-mono text-foreground" data-testid="vb-msg-at" />
            <Button size="sm" variant="outline" onClick={schedule} disabled={!!busy || !at} className="h-7 text-[11px]" data-testid="vb-msg-schedule-btn">
              {busy === 'schedule' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CalendarClock className="w-3 h-3 mr-1" />} Planen
            </Button>
          </div>
        </div>
        <div className="flex flex-col items-center gap-2">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Live-Vorschau</p>
          <NoteGrid matrix={preview} size="md" testid="vb-msg-preview" />
          {scheduled.length > 0 && (
            <div className="w-full mt-2" data-testid="vb-msg-scheduled">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Geplant · {scheduled.length}</p>
              {scheduled.map(m => (
                <div key={m.id} className="flex items-center justify-between text-xs border-b border-border/50 py-1" data-testid={`vb-msg-scheduled-${m.id}`}>
                  <span className="text-foreground truncate">{fmtDT(m.at)} · <span className="font-mono text-muted-foreground">{(m.payload?.rows || []).filter(Boolean).join(' / ') || '▓ Kacheln'}</span> · {Math.round(m.display_seconds / 60)} Min</span>
                  <button onClick={() => unschedule(m.id)} className="text-muted-foreground hover:text-red-400" data-testid={`vb-msg-unschedule-${m.id}`}><X className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
